import React, { useState } from "react";
import {
  Grid,
  Typography,
  Divider,
  TextField,
  Button,
  Stack,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import { generateSecurePassword } from "../../../shared/Utility/passwordGenerator";

export default function TenantAdminCredentialsSection({
  adminUsername,
  form,
  setForm,
  saving,
}) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const passwordsMismatch =
    !!form.admin_password2 && form.admin_password !== form.admin_password2;

  const pwdType = showPassword ? "text" : "password";

  const handleGeneratePassword = () => {
    const generated = generateSecurePassword(18);
    setForm({
      ...form,
      admin_password: generated,
      admin_password2: generated,
    });
    setShowPassword(true); // show immediately so user can see it
  };

  const handleCopyPassword = async () => {
    const value = form.admin_password || "";
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (e) {
      // fallback
      const el = document.createElement("textarea");
      el.value = value;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
  };

  const endAdornmentToggle = (
    <InputAdornment position="end">
      <Tooltip
        title={showPassword ? t("common.hide", "Skryť") : t("common.show", "Zobraziť")}
      >
        <IconButton
          onClick={() => setShowPassword((s) => !s)}
          edge="end"
          disabled={saving}
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  return (
    <>
      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        {t(
          "tenantAdminCredentialsSection.title",
          "Prihlasovacie údaje administrátora"
        )}
      </Typography>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t(
              "tenantAdminCredentialsSection.adminUsername.label",
              "Admin používateľské meno"
            )}
            value={adminUsername}
            disabled
            helperText={t(
              "tenantAdminCredentialsSection.adminUsername.helper",
              "Toto je automaticky vygenerované používateľské meno."
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} />

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type={pwdType}
            label={t(
              "tenantAdminCredentialsSection.adminPassword.label",
              "Heslo administrátora"
            )}
            name="admin_password"
            value={form.admin_password}
            onChange={(e) =>
              setForm({ ...form, admin_password: e.target.value })
            }
            disabled={saving}
            InputProps={{
              endAdornment: endAdornmentToggle,
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type={pwdType}
            label={t(
              "tenantAdminCredentialsSection.repeatPassword.label",
              "Zopakovať heslo"
            )}
            name="admin_password2"
            value={form.admin_password2}
            onChange={(e) =>
              setForm({ ...form, admin_password2: e.target.value })
            }
            disabled={saving}
            error={passwordsMismatch}
            helperText={
              passwordsMismatch
                ? t(
                    "tenantAdminCredentialsSection.repeatPassword.mismatch",
                    "Heslá sa nezhodujú."
                  )
                : " "
            }
            InputProps={{
              endAdornment: endAdornmentToggle,
            }}
          />
        </Grid>

        {/* Action row */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={handleGeneratePassword}
              disabled={saving}
              startIcon={<AutorenewIcon />}
            >
              {t("tenantAdminCredentialsSection.generate", "Generovať heslo")}
            </Button>

            <Button
              variant="outlined"
              onClick={handleCopyPassword}
              disabled={saving || !form.admin_password}
              startIcon={<ContentCopyIcon />}
            >
              {t("tenantAdminCredentialsSection.copy", "Kopírovať")}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}